import React, { useRef, useCallback, useEffect, useState } from 'react';
import { markdownToHtml } from '../../utils/formatText';
import { TEXT_ACCENT_COLORS } from '../../constants/colors';

/* ─── Color palette — sourced from constants/colors.js ─── */
const COLORS = TEXT_ACCENT_COLORS;

/* ─── Sanitizer ─── */

/**
 * Whitelist: strong, b → strong | em, i → em | s, strike, del → s | br
 *            span[style=color] | font[color] → span[style=color]
 * Everything else is stripped; only `color` CSS property kept.
 * div/p → <br>. Leading <br>s stripped, 3+ <br>s collapsed to 2.
 *
 * Exported for use by InlineEditable (preview inline editing).
 */
export function sanitizeHtml(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');

  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const tag = node.tagName.toLowerCase();
    const children = Array.from(node.childNodes).map(walk).join('');

    if (tag === 'strong' || tag === 'b')
      return children ? `<strong>${children}</strong>` : '';
    if (tag === 'em' || tag === 'i')
      return children ? `<em>${children}</em>` : '';
    if (tag === 's' || tag === 'strike' || tag === 'del')
      return children ? `<s>${children}</s>` : '';
    if (tag === 'br') return '<br>';

    // span with color style
    if (tag === 'span') {
      const color = node.style && node.style.color;
      if (color && children) {
        return `<span style="color:${color}">${children}</span>`;
      }
      return children; // strip span without color
    }

    // font with color attribute (legacy execCommand output)
    if (tag === 'font') {
      const color = node.getAttribute('color');
      if (color && children) {
        return `<span style="color:${color}">${children}</span>`;
      }
      return children;
    }

    // div/p → line break
    if (tag === 'div' || tag === 'p') return `<br>${children}`;

    return children;
  }

  let result = Array.from(doc.body.childNodes).map(walk).join('');
  result = result.replace(/^(<br>)+/, '');
  result = result.replace(/(<br>){3,}/g, '<br><br>');
  return result;
}

/* ─── Helpers ─── */

function getTextLength(html) {
  if (!html) return 0;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').length;
}

function valueToHtml(value) {
  if (!value) return '';
  if (/<(\w+)[\s>]/i.test(value)) return value;          // already HTML
  if (/\*{1,3}[^*]+\*{1,3}/.test(value)) {              // legacy markdown
    let html = markdownToHtml(value);
    return html.replace(/\n/g, '<br>');
  }
  return value.replace(/\n/g, '<br>');                     // plain text
}

function placeCursorAtEnd(el) {
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
}

/* ─── Component ─── */

export function FormattedTextarea({
  value,
  onValueChange,
  className,
  rows = 6,
  maxLength,
  placeholder,
  align,
  onAlignChange,
}) {
  const editorRef = useRef(null);
  const internalRef = useRef(value || '');
  const composingRef = useRef(false);
  const [isEmpty, setIsEmpty] = useState(!value);
  const [charCount, setCharCount] = useState(0);
  const colorInputRef = useRef(null);
  const savedSelectionRef = useRef(null);

  /* ── External sync ── */
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = valueToHtml(value);
    if (html !== internalRef.current) {
      const focused = document.activeElement === el;
      let saved = null;
      if (focused) {
        const sel = window.getSelection();
        if (sel.rangeCount) saved = sel.getRangeAt(0).cloneRange();
      }
      el.innerHTML = html;
      internalRef.current = html;
      setIsEmpty(!html || html === '<br>');
      setCharCount(getTextLength(html));
      if (focused && saved) {
        try { const s = window.getSelection(); s.removeAllRanges(); s.addRange(saved); }
        catch { placeCursorAtEnd(el); }
      }
    }
  }, [value]);

  /* ── Mount ── */
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = valueToHtml(value);
    el.innerHTML = html;
    internalRef.current = html;
    setIsEmpty(!html || html === '<br>');
    setCharCount(getTextLength(html));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Input handler ── */
  const handleInput = useCallback(() => {
    if (composingRef.current) return;
    const el = editorRef.current;
    if (!el) return;

    let html = sanitizeHtml(el.innerHTML);
    const len = getTextLength(html);

    if (maxLength && len > maxLength) {
      el.innerHTML = internalRef.current;
      placeCursorAtEnd(el);
      return;
    }

    setCharCount(len);
    internalRef.current = html;
    setIsEmpty(!html || html === '<br>');
    onValueChange(html);
  }, [maxLength, onValueChange]);

  const onCompStart = useCallback(() => { composingRef.current = true; }, []);
  const onCompEnd = useCallback(() => { composingRef.current = false; handleInput(); }, [handleInput]);

  /* ── Paste (plain text only) ── */
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
  }, []);

  /* ── Enter → <br> ── */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') { e.preventDefault(); document.execCommand('insertLineBreak'); }
  }, []);

  /* ── Format commands ── */
  const exec = useCallback((cmd, val) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val ?? null);
    // Give the browser a tick to update the DOM, then sync
    setTimeout(() => handleInput(), 0);
  }, [handleInput]);

  const applyBold        = useCallback(() => exec('bold'),          [exec]);
  const applyItalic      = useCallback(() => exec('italic'),        [exec]);
  const applyStrike      = useCallback(() => exec('strikeThrough'), [exec]);
  const applyColor = useCallback((hex) => {
    editorRef.current?.focus();
    document.execCommand('foreColor', false, hex);
    setTimeout(() => handleInput(), 0);
  }, [handleInput]);

  /* Reset: strip ALL formatting from the entire field → plain text */
  const resetFormat = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    // Extract plain text, preserving line breaks
    const plainText = el.innerText || '';
    // Convert back to simple HTML (only <br> for newlines)
    const html = plainText.replace(/\n/g, '<br>');
    el.innerHTML = html;
    placeCursorAtEnd(el);
    setTimeout(() => handleInput(), 0);
  }, [handleInput]);

  /* ── Custom color picker (⊕) ── */
  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    if (savedSelectionRef.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);
    }
  }, []);

  const handleColorPickerOpen = useCallback(() => {
    saveSelection();
    colorInputRef.current?.click();
  }, [saveSelection]);

  const handleColorPickerChange = useCallback((e) => {
    const hex = e.target.value;
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand('foreColor', false, hex);
    setTimeout(() => handleInput(), 0);
  }, [restoreSelection, handleInput]);

  /* ── Eyedropper ── */
  const eyedropperSupported = typeof window !== 'undefined' && 'EyeDropper' in window;

  const handleEyedropperPick = useCallback(async () => {
    if (!eyedropperSupported) return;
    saveSelection();
    try {
      const dropper = new window.EyeDropper();
      const result = await dropper.open();
      const hex = result.sRGBHex;
      editorRef.current?.focus();
      restoreSelection();
      document.execCommand('foreColor', false, hex);
      setTimeout(() => handleInput(), 0);
    } catch {
      // User cancelled
    }
  }, [eyedropperSupported, saveSelection, restoreSelection, handleInput]);

  const minHeight = Math.max(rows * 24, 48);

  return (
    <div>
      {/* ── Toolbar ── */}
      <div className="flex items-center gap-1 mb-1.5 flex-wrap">
        {/* Bold */}
        <ToolBtn onMouseDown={prevent} onClick={applyBold} title="Bold" className="font-bold">
          B
        </ToolBtn>
        {/* Italic */}
        <ToolBtn onMouseDown={prevent} onClick={applyItalic} title="Italic" className="italic">
          I
        </ToolBtn>
        {/* Strikethrough */}
        <ToolBtn onMouseDown={prevent} onClick={applyStrike} title="Strikethrough" className="line-through">
          S
        </ToolBtn>

        {/* Colors */}
        {COLORS.map((c) => (
          <ToolBtn
            key={c.hex}
            onMouseDown={prevent}
            onClick={() => applyColor(c.hex)}
            title={c.label}
          >
            <span
              className={`h-3 w-3 rounded-full${c.hex === '#FFFFFF' || c.hex === '#000000' ? ' ring-1 ring-white/30' : ''}`}
              style={{ backgroundColor: c.hex }}
            />
          </ToolBtn>
        ))}

        {/* Custom color picker */}
        <ToolBtn onMouseDown={prevent} onClick={handleColorPickerOpen} title="More colours">
          <span style={{ fontSize: '14px', lineHeight: 1 }}>⊕</span>
        </ToolBtn>
        <input
          ref={colorInputRef}
          type="color"
          className="sr-only"
          tabIndex={-1}
          onChange={handleColorPickerChange}
        />

        {/* Eyedropper */}
        {eyedropperSupported && (
          <ToolBtn onMouseDown={prevent} onClick={handleEyedropperPick} title="Pick colour from screen">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m2 22 .414-.414" />
              <path d="m12 9-8.414 8.414A2 2 0 0 0 3 18.828v1.344a2 2 0 0 1-.586 1.414A2 2 0 0 1 3.828 21h1.344a2 2 0 0 0 1.414-.586L15 12" />
              <path d="m18 9 .4.4a1 1 0 1 1-3 3l-3.8-3.8a1 1 0 1 1 3-3l.4.4 3.4-3.4a1 1 0 1 1 3 3z" />
            </svg>
          </ToolBtn>
        )}

        {/* Clear formatting — clean counterclockwise arrow */}
        <ToolBtn onMouseDown={prevent} onClick={resetFormat} title="Clear formatting">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 6.5C2.8 4 5.2 2.2 8 2.2A5.8 5.8 0 1 1 2.8 10.5" />
            <path d="M2 2.5v4h4" />
          </svg>
        </ToolBtn>

        {/* Text alignment */}
        {onAlignChange && (
          <>
            <div className="w-px h-4 bg-white/10 mx-0.5" />
            <ToolBtn onMouseDown={prevent} onClick={() => onAlignChange('left')} title="Align left"
              className={align === 'left' || !align ? 'bg-white/15 text-white' : ''}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <line x1="1" y1="3" x2="15" y2="3" /><line x1="1" y1="7" x2="10" y2="7" /><line x1="1" y1="11" x2="13" y2="11" /><line x1="1" y1="15" x2="8" y2="15" />
              </svg>
            </ToolBtn>
            <ToolBtn onMouseDown={prevent} onClick={() => onAlignChange('center')} title="Align center"
              className={align === 'center' ? 'bg-white/15 text-white' : ''}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <line x1="1" y1="3" x2="15" y2="3" /><line x1="3.5" y1="7" x2="12.5" y2="7" /><line x1="2" y1="11" x2="14" y2="11" /><line x1="4.5" y1="15" x2="11.5" y2="15" />
              </svg>
            </ToolBtn>
            <ToolBtn onMouseDown={prevent} onClick={() => onAlignChange('right')} title="Align right"
              className={align === 'right' ? 'bg-white/15 text-white' : ''}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <line x1="1" y1="3" x2="15" y2="3" /><line x1="6" y1="7" x2="15" y2="7" /><line x1="3" y1="11" x2="15" y2="11" /><line x1="8" y1="15" x2="15" y2="15" />
              </svg>
            </ToolBtn>
          </>
        )}

        {/* Char counter */}
        <span className="ml-auto text-[10px] text-white/25 tabular-nums whitespace-nowrap">
          {maxLength ? `${charCount}/${maxLength}` : charCount}
        </span>
      </div>

      {/* ── Editor ── */}
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onCompositionStart={onCompStart}
          onCompositionEnd={onCompEnd}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          className={className || 'textarea-base'}
          style={{
            minHeight: `${minHeight}px`,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowY: 'auto',
          }}
          data-placeholder={placeholder}
        />
        {isEmpty && placeholder && (
          <div
            className="absolute top-0 left-0 right-0 pointer-events-none"
            style={{ padding: '10px 12px', fontSize: 13, color: 'rgba(255,255,255,0.25)', whiteSpace: 'pre-wrap' }}
          >
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Toolbar button ── */
function ToolBtn({ children, className = '', ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`h-7 w-7 rounded-lg border border-white/10 bg-white/5 hover:bg-white/15 text-white/70 hover:text-white text-xs transition-colors flex items-center justify-center ${className}`}
    >
      {children}
    </button>
  );
}

function prevent(e) { e.preventDefault(); }
